import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Card, CardContent, Grid } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LayersIcon from '@mui/icons-material/Layers';
import DescriptionIcon from '@mui/icons-material/Description';
import { formattedAmount } from 'utils/formattedAmount';

type JsonObject = Record<string, string | number | null | undefined>;

interface JsonTableProps {
  title: string;
  data: JsonObject[] | JsonObject | null;
  customHeaders?: string[];
  footerRow?: { [key: string]: string | number }; 
}

interface InfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  width:number;
}

const InfoCard: React.FC<InfoCardProps> = ({ icon, label, value, width }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        width: width,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 3,
        borderRadius: 3,
      }}
    >
      <Box sx={{ mr: 2, color: theme.palette.primary.main }}>{icon}</Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {value}
        </Typography>
      </Box>
    </Card>
  );
};

interface InfoCardsProps {
  clientName: string;
  quarter: string;
  financialYear: string;
  formType: string;
}



const InfoCards: React.FC<InfoCardsProps> = ({ clientName, quarter, financialYear, formType }) => {
  return (
    <Box sx={{ overflowY: 'auto', maxHeight: '100vh', p: 2 }}>
      <Grid container spacing={3}>
        <Grid item>
          <InfoCard icon={<BusinessIcon />} label="Client Name" value={clientName} width={550}/>
        </Grid>
        <Grid item>
          <InfoCard icon={<CalendarMonthIcon />} label="Financial Year" value={financialYear} width={200} />
        </Grid>
         <Grid item>
          <InfoCard icon={<LayersIcon />} label="Quarter" value={quarter} width={150} />
        </Grid>
        <Grid item>
          <InfoCard icon={<DescriptionIcon />} label="Form Type" value={formType} width={150}/>
        </Grid>
      </Grid>
    </Box>
  );
};

const JsonTable: React.FC<JsonTableProps> = ({ title, data,customHeaders,footerRow }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data || (Array.isArray(data) && data.length === 0)) return null;

  const rows = Array.isArray(data) ? data : [data];
 const defaultHeaders = rows.length > 0 ? Object.keys(rows[0]) : [];
const headers = customHeaders ?? defaultHeaders;


  return (
    <Box sx={{ my: 3, px: 2 }}>
      <Typography
        variant="subtitle2"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#0d47a1',
        //   backgroundColor: '#e3f2fd',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          display: 'inline-block',
        }}
      >
        {title}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          overflowX: 'auto',
          maxHeight: 700,
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#1b4670',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    height: 36,
                    whiteSpace: 'nowrap',
                    borderRight: '1px solid #e0e0e0',
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={idx}
                sx={{
                  backgroundColor: idx % 2 === 0 ? '#ffffff' : '#dde9f3ff',
                  '& td': { fontSize: '0.75rem', height: 34, padding: '6px 12px' },
                }}
              >
                {headers.map((header) => (
                  <TableCell key={header}>
                    {row[header] !== '' && row[header] !== undefined && row[header] !== null
                      ? row[header]
                      : '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {footerRow && (
  <TableRow
    sx={{
      backgroundColor: '#f5f5f5',
      '& td': { fontWeight: 'bold', fontSize: '0.75rem', padding: '6px 12px' },
    }}
  >
    {headers.map((header, idx) => (
      <TableCell key={header}>
        {footerRow[header] !== undefined ? footerRow[header] : idx === 0 ? 'Net Payable (Rounded-Off) (Rs.)' : ''}
      </TableCell>
    ))}
  </TableRow>
)}

        </Table>
      </TableContainer>
    </Box>
  );
};

interface TraceOutstandingDemandTablesProps {
  tokenSummary: JsonObject[] | null;
  demandSummary: JsonObject[] | null;
  panErrorSummary: JsonObject | null;
  correctionSummary: JsonObject | null;
  information: any
}

const TraceOutstandingDemandTables: React.FC<TraceOutstandingDemandTablesProps> = ({
  tokenSummary,
  demandSummary,
  panErrorSummary,
  correctionSummary,
  information
}) => {
    const demandHeaders = [
  'Sr.No.',
  'Type of Default',
  'Default Amount (Rs.)',
  "Amount Reported As 'Interest / Others' Claimed in the Statement (Rs.)",
  'Payable (Rs.)',
];

    const statementHeaders = [
  'Count of Correction Statement(s)',
  'Net Payable (Rounded-Off) (Rs.)',
];

const demandFooter = {
  'Payable (Rs.)': formattedAmount(information?.netPayableAmount) ?? '-',
};
  return (
    <Box sx={{ height: '80vh', overflow: 'auto', px: 2, py: 4, }}>
        <InfoCards
  clientName={information?.tanClientCredentials?.client?.displayName}
  quarter={information.quarter}
  financialYear={information.financialYear}
  formType={information.formType}
/>
      <JsonTable title="Statement Summary" data={tokenSummary} />
      <Box paddingLeft={3} paddingRight={1}>
        <Typography>* Details of maximum 2 latest processed correction statement(s) will be displayed. For details of other correction statement(s), if any, refer prior intimations sent by TDS CPC.</Typography>
      </Box>
    
      <JsonTable title="Correction Statement Summary" data={correctionSummary} customHeaders={statementHeaders}/>
      <JsonTable title="Default Summary Details" data={demandSummary} customHeaders={demandHeaders} footerRow={demandFooter}/>
      <JsonTable title="Summary of PAN Errors" data={panErrorSummary} />
    </Box>
  );
};

export default TraceOutstandingDemandTables;
